import { Business, Person, Star } from '@mui/icons-material';
import { Box, Card, CardContent, Divider, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SimpleAlert from '../../../../../components/display/SimpleAlert';
import Skeletons from '../../../../../components/loader/Skeletons';
import { useFetch } from '../../../../../hooks/Fetching';
import { useIsSmallerScreen } from '../../../../../utils/Functions';

const StatsCard = ({ title, data, icon, average }) => {
  const { t } = useTranslation();
  const isMobile = useIsSmallerScreen();

  const totalRatings = Object.values(data).reduce((a, b) => a + b, 0);
  const averageRating = Number(average);
  const percentage = (averageRating / 5) * 100;

  return (
    <Card sx={{ 
      height: '100%',
      borderRadius: 2,
      boxShadow: isMobile ? 1 : 3,
      '&:hover': {
        boxShadow: isMobile ? 2 : 4,
      },
      transition: 'all 0.3s ease'
    }}>
      <CardContent sx={{ p: isMobile ? 2 : 3 }}>
        <Box display="flex" alignItems="center" mb={isMobile ? 1.5 : 2}>
          {icon}
          <Typography 
            variant={isMobile ? "subtitle1" : "h6"} 
            sx={{ ml: 1, fontWeight: isMobile ? 600 : 700 }}
          >
            {t(title)}
          </Typography>
        </Box>
        
        <Box 
          display="flex" 
          alignItems="center" 
          justifyContent="space-between"
          flexDirection={isMobile ? "column" : "row"}
          gap={isMobile ? 2 : 0}
          mb={isMobile ? 2 : 3}
        >
          {/* Circular Progress */}
          <Box position="relative" width={isMobile ? 80 : 100} height={isMobile ? 80 : 100}>
            <Box
              position="absolute"
              top={0}
              left={0}
              width="100%"
              height="100%"
              borderRadius="50%"
              bgcolor="rgba(0, 0, 0, 0.1)"
            />
            <Box
              position="absolute"
              top={0}
              left={0}
              width="100%"
              height="100%"
              borderRadius="50%"
              sx={{
                background: `conic-gradient(#1976d2 ${percentage}%, transparent ${percentage}% 100%)`,
                clipPath: 'circle(50% at 50% 50%)',
              }}
            />
            <Box
              position="absolute"
              top={isMobile ? 8 : 10}
              left={isMobile ? 8 : 10}
              width={isMobile ? 64 : 80}
              height={isMobile ? 64 : 80}
              borderRadius="50%"
              bgcolor="background.paper"
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
            >
              <Typography 
                variant={isMobile ? "h5" : "h4"} 
                fontWeight="bold"
                sx={{ fontSize: isMobile ? '1.5rem' : '2rem' }}
              >
                {averageRating.toFixed(1)}
              </Typography>
              <Box display="flex" alignItems="center">
                <Star color="primary" fontSize={isMobile ? "small" : "medium"} />
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                >
                  /5
                </Typography>
              </Box>
            </Box>
          </Box>
          
          {/* Rating Bars */}
          <Box sx={{ 
            width: isMobile ? '100%' : 'auto',
            maxWidth: isMobile ? '100%' : 250 
          }}>
            <Typography 
              variant={isMobile ? "caption" : "subtitle2"} 
              color="text.secondary" 
              gutterBottom
              sx={{ 
                textAlign: isMobile ? 'center' : 'left',
                fontSize: isMobile ? '0.75rem' : '0.875rem'
              }}
            >
              {t("TotalNumberOfRatings")}: {totalRatings}
            </Typography>
            
            <Box sx={{ 
              maxHeight: isMobile ? 200 : 'none',
              overflowY: isMobile ? 'auto' : 'visible'
            }}>
              {Object.entries(data).map(([key, value]) => {
                const ratingValue = parseInt(key);
                const percentage = totalRatings > 0 ? (value / totalRatings) * 100 : 0;
                
                return (
                  <Box key={key}>
                    <Box 
                      display="flex" 
                      alignItems="center" 
                      mb={1} 
                      mt={1}
                      sx={{ 
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: isMobile ? 0.5 : 0
                      }}
                    >
                      <Box width={isMobile ? '100%' : 100}>
                        <Typography 
                          variant={isMobile ? "caption" : "body2"}
                          sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                        >
                          {key} {t(ratingValue === 1 ? "Star" : "Stars")}
                        </Typography>
                      </Box>
                      
                      <Box 
                        width={isMobile ? '100%' : 250} 
                        px={isMobile ? 0 : 1}
                        display="flex"
                        alignItems="center"
                        gap={1}
                      >
                        <Box 
                          flex={1}
                          height={isMobile ? 6 : 8} 
                          bgcolor="primary.main" 
                          borderRadius={4}
                          sx={{ 
                            width: `${percentage}%`,
                            minWidth: percentage > 0 ? '4px' : '0px'
                          }}
                        />
                        <Typography 
                          variant={isMobile ? "caption" : "body2"}
                          sx={{ 
                            minWidth: isMobile ? 20 : 24,
                            textAlign: 'right',
                            fontSize: isMobile ? '0.7rem' : '0.875rem'
                          }}
                        >
                          {value}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {ratingValue > 1 && <Divider sx={{ my: 0.5 }} />}
                  </Box>
                );
              }).reverse()}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default function Ratings() {
    const api = '/feedback/overall';
    const key = ["overallfeedbacks_q"];
    const { data, isLoading, error } = useFetch(api, null, key);
    const isMobile = useIsSmallerScreen();

    if (isLoading) {
        return (
            <Box sx={{ p: isMobile ? 1 : 2 }}>
                <Skeletons 
                    numOfSkeletons={3} 
                    lg={12} 
                    md={12}
                    sm={12}
                    height={isMobile ? 300 : 350} 
                    width="100%"
                />
            </Box>
        );
    } else if (!isLoading && error) {
        return <SimpleAlert message={"ErrorFetchingOverallRatings"} severity={"error"} />;
    }

    const averageClinicRating = data["averageClinicRating"]?.average || 0;
    const averageUserRating = data["averageUserRating"]?.average || 0;
    const averageOverallRating = data["averageOverallRating"]?.average || 0;
    const clinicRatings = data["clinicRatings"] || {};
    const userRatings = data["userRatings"] || {};
    const overallRatings = data["overallRatings"] || {};

    return (
        <Grid 
            container 
            spacing={isMobile ? 2 : 3} 
            sx={{ 
                p: isMobile ? 1 : 2,
                maxWidth: '100%',
                margin: 0
            }}
        >
            <Grid item xs={12} md={4}>
                <StatsCard 
                    title="UserRatings" 
                    data={userRatings} 
                    average={averageUserRating}
                    icon={<Person color="primary" fontSize={isMobile ? "small" : "medium"} />}
                />
            </Grid>
            <Grid item xs={12} md={4}>
                <StatsCard 
                    title="ClinicRatings" 
                    data={clinicRatings} 
                    average={averageClinicRating}
                    icon={<Business color="primary" fontSize={isMobile ? "small" : "medium"} />}
                />
            </Grid>
            <Grid item xs={12} md={4}>
                <StatsCard 
                    title="OverallRatings" 
                    data={overallRatings} 
                    average={averageOverallRating}
                    icon={<Star color="primary" fontSize={isMobile ? "small" : "medium"} />}
                />
            </Grid>
        </Grid>
    );
}